/**
 * "Show more" pagination for collection grids. Each click requests the next page of the section
 * and appends its product cards to the grid that is already on screen, so a six page collection
 * is reached in five clicks instead of five page loads.
 *
 * Only product cards are carried across. The collection hero tile and the featured product card
 * blocks are positioned relative to the start of a page, so every page's markup contains them
 * and copying them over would duplicate them further down the grid.
 *
 * The loaded pages and scroll position are kept on the history entry, so coming back to the
 * collection (browser back from a product page, or a reload) refills the grid where it was left.
 */
if (!customElements.get('collection-show-more')) {
  /* Only the first grid built after a page load jumps to the saved scroll position. A grid rebuilt
     later by the filters' own back and forward handling leaves the scroll alone. */
  let scrollRestorePending = true;

  customElements.define(
    'collection-show-more',
    class CollectionShowMore extends HTMLElement {
      connectedCallback() {
        if (this.button) return;

        this.button = this.querySelector('.collection-show-more__button');
        this.status = this.querySelector('.collection-show-more__status');
        if (this.button) this.button.addEventListener('click', this.loadNextPage.bind(this));
        this.loadedPages = [];

        const saved = this.savedState();
        const restoreScroll = scrollRestorePending;
        scrollRestorePending = false;

        if (saved) {
          this.restore(saved, restoreScroll);
        } else {
          this.prefetch();
        }
      }

      disconnectedCallback() {
        if (this.onLeave) window.removeEventListener('pagehide', this.onLeave);
        if (this.onLinkClick) document.removeEventListener('click', this.onLinkClick, true);
        this.onLeave = null;
        this.onLinkClick = null;
      }

      get grid() {
        return (this.closest('.collection') || document).querySelector('collection-grid');
      }

      /* Requesting the page through the Section Rendering API keeps the payload to this section
         instead of a whole collection page. The URL already carries any active filters and sort. */
      sectionUrl(pageUrl) {
        const url = new URL(pageUrl, window.location.origin);
        if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);
        return url.toString();
      }

      /* Products listed in the theme's hidden products setting still count towards pagination but
         are left out of the grid, so a later page can render no cards at all. The next page with
         visible products is fetched ahead of the click: if there is none, the button is hidden
         straight away rather than staying on screen after every visible product is shown. */
      prefetch() {
        if (!this.dataset.url) return;

        const pending = this.findNextProducts(this.dataset.url);
        this.pending = pending;

        pending
          .then((result) => {
            if (this.pending === pending && !result.page) this.complete();
          })
          .catch(() => {
            /* Left to the click, which retries and reports the failure. */
            if (this.pending === pending) this.pending = null;
          });
      }

      async findNextProducts(pageUrl) {
        let url = pageUrl;

        while (url) {
          const response = await fetch(this.sectionUrl(url));
          if (!response.ok) throw new Error(`Show more request failed with status ${response.status}`);

          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = page.querySelector('collection-show-more');
          const nextUrl = next && next.dataset.url ? next.dataset.url : null;

          if (page.querySelector('collection-grid .grid__item--product')) return { page, pageUrl: url, nextUrl };
          url = nextUrl;
        }

        return { page: null, pageUrl: null, nextUrl: null };
      }

      async loadNextPage() {
        if (this.loading || !this.dataset.url) return;
        this.setLoading(true);

        try {
          if (!this.pending) this.prefetch();
          const { page, pageUrl, nextUrl } = await this.pending;
          this.pending = null;

          const added = page ? this.appendProducts(page) : 0;
          this.announce(`${added} more product${added === 1 ? '' : 's'} loaded.`);
          if (page) this.loadedPages.push(pageUrl);

          if (nextUrl) {
            this.dataset.url = nextUrl;
            this.prefetch();
          } else {
            this.complete();
          }

          this.saveState();
        } catch (error) {
          console.error(error);
          this.announce('Sorry, more products could not be loaded. Please try again.', true);
        } finally {
          this.setLoading(false);
        }
      }

      /* The state sits next to whatever the filters keep on the same entry (their search params),
         so it is merged in rather than replacing it. */
      savedState() {
        const saved = history.state && history.state.collectionShowMore;
        if (!saved || saved.sectionId !== this.dataset.sectionId || !Array.isArray(saved.pages)) return null;
        return saved.pages.length > 0 ? saved : null;
      }

      saveState(scrollY) {
        if (this.loadedPages.length === 0) return;

        const previous = (history.state && history.state.collectionShowMore) || {};
        const collectionShowMore = {
          sectionId: this.dataset.sectionId,
          pages: this.loadedPages,
          nextUrl: this.dataset.url || null,
          scrollY: typeof scrollY === 'number' ? scrollY : previous.scrollY || 0,
        };

        try {
          history.replaceState({ ...(history.state || {}), collectionShowMore }, '');
        } catch (error) {
          console.error(error);
        }

        this.trackLeaving();
      }

      /* The scroll position is recorded on the way out. A product link click covers browsers that
         don't let pagehide touch the history entry. Left to itself, the browser would restore the
         scroll before the extra products are back, so the page takes that over. */
      trackLeaving() {
        if (this.onLeave) return;

        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

        this.onLeave = () => this.saveState(window.scrollY);
        this.onLinkClick = (event) => {
          if (event.target.closest && event.target.closest('a[href]')) this.onLeave();
        };

        window.addEventListener('pagehide', this.onLeave);
        document.addEventListener('click', this.onLinkClick, true);
      }

      async restore(saved, restoreScroll) {
        this.setLoading(true);

        try {
          const pages = await Promise.all(
            saved.pages.map(async (pageUrl) => {
              const response = await fetch(this.sectionUrl(pageUrl));
              if (!response.ok) throw new Error(`Show more request failed with status ${response.status}`);
              return new DOMParser().parseFromString(await response.text(), 'text/html');
            })
          );

          pages.forEach((page) => this.appendProducts(page));
          this.loadedPages = saved.pages.slice();

          if (saved.nextUrl) {
            this.dataset.url = saved.nextUrl;
            this.prefetch();
          } else {
            this.complete();
          }

          this.saveState();
          if (restoreScroll && saved.scrollY) window.scrollTo(0, saved.scrollY);
        } catch (error) {
          /* Fall back to the first page as it was rendered, with show more working as normal. */
          console.error(error);
          this.prefetch();
        } finally {
          this.setLoading(false);
        }
      }

      appendProducts(nextPage) {
        const grid = this.grid;
        const items = nextPage.querySelectorAll('collection-grid .grid__item--product');
        if (!grid || items.length === 0) return 0;

        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          /* The reveal-on-scroll observer only watches elements that were in the document at
             load, so anything appended afterwards would stay stuck at its hidden start state. */
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
          fragment.appendChild(item);
        });

        grid.appendChild(fragment);
        this.bindShadeSwatches(grid);

        return items.length;
      }

      /* Shade pickers are wired up per card as the page loads, so appended cards need the same. */
      bindShadeSwatches(grid) {
        grid.querySelectorAll('.product-card-wrapper').forEach((card) => {
          const trigger = card.querySelector('.select-shade.btn');
          const options = card.querySelector('.product__card-options');
          if (!trigger || !options || trigger.dataset.shadeBound) return;

          trigger.dataset.shadeBound = 'true';
          trigger.addEventListener('click', () => {
            options.classList.add('show');
            trigger.style.opacity = '0';
          });
        });
      }

      complete() {
        delete this.dataset.url;
        this.pending = null;
        this.button.hidden = true;
        this.classList.add('collection-show-more--complete');
      }

      setLoading(loading) {
        this.loading = loading;
        this.classList.toggle('collection-show-more--loading', loading);
        this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.button.disabled = loading;
      }

      announce(message, isError = false) {
        if (!this.status) return;

        this.status.textContent = message;
        this.status.classList.toggle('collection-show-more__status--error', isError);
      }
    }
  );
}
